import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://zedro.academy";
const SITE_NAME = "ZEDRO Academy";

const DEFAULT_SEO = {
  title: "ZEDRO Academy | Formación práctica con fundamento cristiano",
  description:
    "Formación práctica con fundamento cristiano para conectar principios con decisiones, acciones y evidencia, con una visión que integra vida, liderazgo y empresa.",
  robots: "index, follow",
  lang: "es",
};

const STATIC_ROUTES = {
  "/": DEFAULT_SEO,

  "/metodologia": {
    title: "Metodología ZEDRO | Comprender, decidir y actuar",
    description:
      "Conoce cómo ZEDRO Academy organiza cada recorrido para comprender, tomar decisiones, actuar y conservar evidencia del avance con continuidad.",
    robots: "index, follow",
    lang: "es",
  },

  "/store": {
    title: "Membresía ZEDRO Academy | Formación práctica diaria",
    description:
      "Accede a Cimientos ZEDRO, Bitácora y Conexión: formación guiada, práctica diaria, registro del proceso y acompañamiento con fundamento cristiano.",
    robots: "index, follow",
    lang: "es",
  },

  "/zedro-news": {
    title: "ZEDRO News | Ideas para comprender, decidir y actuar",
    description:
      "Publicaciones y recursos de ZEDRO Academy para comprender principios, fortalecer decisiones y llevar lo aprendido a la vida cotidiana.",
    robots: "index, follow",
    lang: "es",
  },

  "/fr/zedro-news": {
    title: "ZEDRO News | Comprendre, décider et agir",
    description:
      "Découvrez les publications et les ressources de ZEDRO Academy pour mieux comprendre, renforcer vos décisions et appliquer les apprentissages au quotidien.",
    robots: "index, follow",
    lang: "fr",
  },

  "/legal/aviso-legal": {
    title: "Aviso legal | Académie ZEDRO",
    description: "Información legal de Académie ZEDRO.",
    robots: "index, follow",
    lang: "es",
  },

  "/legal/privacidad": {
    title: "Política de privacidad | Académie ZEDRO",
    description:
      "Consulta cómo Académie ZEDRO protege y administra la información personal.",
    robots: "index, follow",
    lang: "es",
  },

  "/legal/cookies": {
    title: "Política de cookies | Académie ZEDRO",
    description:
      "Información sobre el uso y la administración de cookies en Académie ZEDRO.",
    robots: "index, follow",
    lang: "es",
  },

  "/legal/terminos": {
    title: "Términos y condiciones | Académie ZEDRO",
    description:
      "Consulta los términos y condiciones de uso de Académie ZEDRO.",
    robots: "index, follow",
    lang: "es",
  },

  "/legal/cancelacion-reembolsos": {
    title: "Cancelaciones y reembolsos | Académie ZEDRO",
    description:
      "Consulta la política de cancelación y reembolsos de Académie ZEDRO.",
    robots: "index, follow",
    lang: "es",
  },

  "/legal/descargo": {
    title: "Descargo de responsabilidad | Académie ZEDRO",
    description:
      "Consulta el descargo de responsabilidad de Académie ZEDRO.",
    robots: "index, follow",
    lang: "es",
  },
};

const PRIVATE_PREFIXES = [
  "/login",
  "/logout",
  "/activate",
  "/forgot-password",
  "/reset-password",
  "/preferences",
  "/unsubscribe",
  "/print",
  "/welcome",
  "/dashboard",
  "/account",
  "/cuenta",
  "/core",
  "/tools",
  "/bitacora",
  "/conexion",
  "/guia",
  "/cultura",
  "/module",
  "/systems",
  "/admin",
];

function setMeta(selector, attribute, value) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  return element;
}

function updateMetaName(name, content) {
  const element = setMeta(`meta[name="${name}"]`, "name", name);
  element.setAttribute("content", content);
}

function updateMetaProperty(property, content) {
  const element = setMeta(
    `meta[property="${property}"]`,
    "property",
    property
  );
  element.setAttribute("content", content);
}

function updateCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function normalizePath(pathname) {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

function getSeo(pathname) {
  const normalizedPath = normalizePath(pathname);

  if (STATIC_ROUTES[normalizedPath]) {
    return STATIC_ROUTES[normalizedPath];
  }

  if (normalizedPath.startsWith("/zedro-news/")) {
    return {
      title: "ZEDRO News | ZEDRO Academy",
      description:
        "Contenido formativo de ZEDRO Academy para comprender principios, fortalecer decisiones y llevar lo aprendido a la vida cotidiana.",
      robots: "index, follow",
      lang: "es",
    };
  }

  if (normalizedPath.startsWith("/fr/zedro-news/")) {
    return {
      title: "ZEDRO News | ZEDRO Academy",
      description:
        "Contenu de formation de ZEDRO Academy pour mieux comprendre, renforcer les décisions et appliquer les apprentissages au quotidien.",
      robots: "index, follow",
      lang: "fr",
    };
  }

  if (normalizedPath.startsWith("/editorial/")) {
    return {
      title: "Editorial | ZEDRO Academy",
      description:
        "Contenido editorial de ZEDRO Academy sobre vida, criterio, liderazgo, decisiones y aplicación práctica.",
      robots: "index, follow",
      lang: "es",
    };
  }

  const isPrivate = PRIVATE_PREFIXES.some(
    (prefix) =>
      normalizedPath === prefix ||
      normalizedPath.startsWith(`${prefix}/`)
  );

  if (isPrivate) {
    return {
      ...DEFAULT_SEO,
      robots: "noindex, nofollow",
    };
  }

  return {
    ...DEFAULT_SEO,
    robots: "noindex, follow",
  };
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePath(pathname);
    const seo = getSeo(normalizedPath);

    const canonicalUrl =
      normalizedPath === "/"
        ? `${SITE_URL}/`
        : `${SITE_URL}${normalizedPath}`;

    document.title = seo.title;
    document.documentElement.lang = seo.lang;

    updateMetaName("description", seo.description);
    updateMetaName("robots", seo.robots);

    updateMetaProperty("og:type", "website");
    updateMetaProperty("og:site_name", SITE_NAME);
    updateMetaProperty("og:title", seo.title);
    updateMetaProperty("og:description", seo.description);
    updateMetaProperty("og:url", canonicalUrl);
    updateMetaProperty("og:locale", seo.lang === "fr" ? "fr_CA" : "es_CA");

    updateMetaName("twitter:card", "summary");
    updateMetaName("twitter:title", seo.title);
    updateMetaName("twitter:description", seo.description);

    updateCanonical(canonicalUrl);
  }, [pathname]);

  return null;
}